import {useEffect, useRef, useState} from 'react'
import {gsap} from 'gsap'

import Styles from './InstallerActionMenu.module.css'

interface UseMonaPixelAnimationOptions {
  enabled: boolean
  prefersReducedMotion: boolean
  resetKey: unknown
}

export function useMonaPixelAnimation({enabled, prefersReducedMotion, resetKey}: UseMonaPixelAnimationOptions) {
  const installerActionMenuRef = useRef<HTMLDivElement>(null)
  const installerButtonOverlayRef = useRef<HTMLDivElement>(null)
  const monaPixelRef = useRef<HTMLImageElement>(null)
  const [downloadStatus, setDownloadStatus] = useState('')

  useEffect(() => {
    if (!enabled) return

    const monaPixel = monaPixelRef.current
    const installerButtonOverlay = installerButtonOverlayRef.current
    const installerButton = installerActionMenuRef.current?.querySelector<HTMLElement>(
      '[data-testid="installer-action-menu-primary-button"]',
    )
    const buttonOverlayItems = installerButtonOverlay?.querySelectorAll<HTMLElement>(`.${Styles.ButtonOverlayItem}`)
    const buttonOverlayText = installerButtonOverlay?.querySelector<HTMLElement>(`.${Styles.ButtonOverlayText}`)
    if (!monaPixel || !installerButtonOverlay || !installerButton || !buttonOverlayItems || !buttonOverlayText) return

    let timeline: gsap.core.Timeline | null = null
    let timer: NodeJS.Timeout | null = null
    let statusAnimationFrame: number | null = null

    const positionMonaPixel = () => {
      const installerActionMenu = installerActionMenuRef.current
      if (!installerActionMenu) return

      const installerButtonRect = installerButton.getBoundingClientRect()
      const monaPixelWidth = monaPixel.offsetWidth

      installerButtonOverlay.style.width = `${installerButtonRect.width}px`
      installerButtonOverlay.style.height = `${installerButtonRect.height}px`
      installerButtonOverlay.style.borderRadius = window.getComputedStyle(installerButton).borderRadius
      monaPixel.style.left = `${installerButtonRect.width / 2 - monaPixelWidth / 2}px`
    }

    const animateMonaPixel = () => {
      setDownloadStatus('')
      if (statusAnimationFrame !== null) window.cancelAnimationFrame(statusAnimationFrame)

      installerButtonOverlay.style.visibility = 'inherit'
      timeline?.kill()

      if (!prefersReducedMotion) {
        timeline = gsap.timeline()
        timeline.set(monaPixel, {autoAlpha: 1})
        timeline.fromTo(
          monaPixel,
          {rotation: -8, scale: 0.8, y: 20},
          {rotation: 0, scale: 1, y: 0, duration: 0.35, ease: 'expo.out'},
          0,
        )

        for (const [index, item] of buttonOverlayItems.entries()) {
          const delay1 = 0.1 * index
          const delay2 = delay1 + 0.1 + 0.1 * (buttonOverlayItems.length - index)

          timeline.fromTo(
            item,
            {transform: 'translateX(-100%)'},
            {
              transform: 'translateX(0%)',
              duration: 0.5,
              ease: 'power3.out',
              onComplete: () => {
                if (index === 0) {
                  buttonOverlayText.style.visibility = 'inherit'
                  statusAnimationFrame = window.requestAnimationFrame(() => setDownloadStatus('Downloaded'))
                }
              },
            },
            delay1,
          )

          timeline.to(item, {transform: 'translateX(100%)', duration: 0.5, ease: 'expo.out'}, delay2)
        }

        timeline.to(
          monaPixel,
          {
            rotation: 8,
            scale: 0.9,
            y: 20,
            autoAlpha: 0,
            duration: 0.2,
            ease: 'power2.in',
            onStart: () => {
              buttonOverlayText.style.visibility = 'hidden'
              installerButtonOverlay.style.visibility = 'hidden'
            },
          },
          2.8,
        )
      } else {
        if (timer) clearTimeout(timer)
        buttonOverlayText.style.visibility = 'inherit'
        statusAnimationFrame = window.requestAnimationFrame(() => setDownloadStatus('Downloaded'))
        monaPixel.style.visibility = 'inherit'
        monaPixel.style.opacity = '1'

        timer = setTimeout(() => {
          buttonOverlayText.style.visibility = 'hidden'
          installerButtonOverlay.style.visibility = 'hidden'
          monaPixel.style.visibility = 'hidden'
          monaPixel.style.opacity = '0'
        }, 3000)
      }
    }

    positionMonaPixel()
    installerButton.addEventListener('click', animateMonaPixel)
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', positionMonaPixel)

    return () => {
      installerButton.removeEventListener('click', animateMonaPixel)
      window.removeEventListener('resize', positionMonaPixel)
      if (statusAnimationFrame !== null) window.cancelAnimationFrame(statusAnimationFrame)
      timeline?.kill()
    }
  }, [enabled, prefersReducedMotion, resetKey])

  return {downloadStatus, installerActionMenuRef, installerButtonOverlayRef, monaPixelRef}
}
